/*
 * Service-worker registration helper.
 *
 * Why a separate file?
 * --------------------
 * Service workers have to be registered exactly once per page-load
 * and the registration call has subtle gotchas (must run after the
 * 'load' event, must be silently no-op'd in dev/StrictMode if not
 * desired, etc.).  Centralising here keeps `main.jsx` clean.
 *
 * Registration is opt-in via two signals:
 *
 *   1. Production builds (``import.meta.env.PROD``) — always on.
 *   2. The ``VITE_ENABLE_SW`` env var — set to "1" / "true" to force-on
 *      in dev when manually testing offline mode.
 *
 * The page also listens for ``message: { type: "FLUSH_DCR_QUEUE" }``
 * coming back from the worker (via Background Sync, see
 * ``public/sw.js``) and dispatches a custom DOM event that the
 * ``PharmaDCR.jsx`` page subscribes to.
 */

export function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  // Dev disable unless explicitly opted in.
  const explicit = String(import.meta.env.VITE_ENABLE_SW || "").toLowerCase();
  const enabled =
    import.meta.env.PROD ||
    explicit === "1" ||
    explicit === "true" ||
    explicit === "yes";
  if (!enabled) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        if (reg && reg.update) {
          // Try to grab a fresher worker on next load.  Errors here
          // are non-fatal; the user can still use the cached app.
          reg.update().catch(() => {});
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[sw] registration failed", err);
      });
  });

  // Fan out FLUSH_DCR_QUEUE messages from the SW into a DOM event so
  // any active page (PharmaDCR.jsx) can react.
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data && event.data.type === "FLUSH_DCR_QUEUE") {
      window.dispatchEvent(new CustomEvent("pharma:flush-dcr-queue"));
    }
  });
}

/**
 * Best-effort "I queued an offline write — please ping me when the
 * network is back" hint to the browser.  Falls back to a manual
 * online listener when Background Sync isn't supported (Safari).
 */
export async function requestDcrFlushSync() {
  if (typeof navigator === "undefined") return;
  try {
    if ("serviceWorker" in navigator && "SyncManager" in window) {
      const reg = await navigator.serviceWorker.ready;
      if (reg && reg.sync) {
        await reg.sync.register("flush-dcr-queue");
        return;
      }
    }
  } catch {
    // Fallthrough — Background Sync not available (Safari et al.).
  }
  // Manual fallback: just dispatch the event when the browser
  // reports the connection has come back.
  if (typeof window !== "undefined") {
    const handler = () => {
      window.removeEventListener("online", handler);
      window.dispatchEvent(new CustomEvent("pharma:flush-dcr-queue"));
    };
    window.addEventListener("online", handler, { once: true });
  }
}
